import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { SiteLogo } from "../components/SiteLogo";
import { LangSwitcher } from "../components/LangSwitcher";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { YouTubeKeyWizard } from "../components/YouTubeKeyWizard";
import { UploadsTab } from "../components/UploadsTab";
import { YouTubeIcon } from "../components/YouTubeIcon";
import { ArrowLeft, LogOut, Trash2, CalendarClock, CreditCard, Loader2, KeyRound, CheckCircle2, AlertTriangle, Layers, ExternalLink } from "lucide-react";
import { t, fmtDateTime } from "../i18n";

const fmtSP = (iso) => fmtDateTime(iso);
const STATUS_LABEL = { ativo: "ativo", expirado: "expirado", desativado: "desativado" };
const daysLeft = (iso) => { try { return Math.ceil((new Date(iso) - Date.now()) / 86400000); } catch { return null; } };

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 py-2.5 text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-medium text-right min-w-0 truncate max-w-[60%]" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [params] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [payments, setPayments] = useState(null);
  const [ytStatus, setYtStatus] = useState(null);
  const [projects, setProjects] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const tab = params.get("tab") || "conta";

  useEffect(() => {
    api.get("/me/profile").then(({ data }) => setProfile(data)).catch(() => toast.error(t("Falha ao carregar perfil")));
    api.get("/me/payments").then(({ data }) => setPayments(data.payments)).catch(() => setPayments([]));
    api.get("/youtube/status").then(({ data }) => setYtStatus(data)).catch(() => {});
    api.get("/overlays").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  const deleteAccount = async () => {
    if (!window.confirm(t("EXCLUIR sua conta LiveGrafX?\n\nSerão apagados: overlays, mídias, fontes, chave do YouTube e conexões (Google/Twitch). Esta ação não pode ser desfeita."))) return;
    if (!window.confirm(t("Tem certeza absoluta? Todos os seus dados serão removidos."))) return;
    try {
      await api.delete("/me");
      toast.success(t("Conta excluída"));
      await logout();
      navigate("/");
    } catch (e) { toast.error(e.response?.data?.detail || t("Falha ao excluir conta")); }
  };

  const removeKey = async () => {
    if (!window.confirm(t("Remover a chave do YouTube da sua conta?"))) return;
    try {
      await api.delete("/youtube/key");
      setYtStatus({ has_key: false, status: "nao_configurada", validada_em: null });
      toast.success(t("Chave removida"));
    } catch { toast.error(t("Falha ao remover")); }
  };

  const d = profile?.access_until ? daysLeft(profile.access_until) : null;
  const statusBadge = {
    ativo: "bg-emerald-500/15 text-emerald-400",
    expirado: "bg-red-500/15 text-red-400",
    desativado: "bg-red-500/15 text-red-400",
  }[profile?.status] || "bg-secondary text-muted-foreground";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-16 border-b border-border/60 flex items-center gap-3 px-6 md:px-10 bg-card">
        <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-foreground" data-testid="profile-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <SiteLogo className="h-8 w-auto" />
        <span className="text-sm font-display font-bold ml-1">· {t("Meu Perfil")}</span>
        <LangSwitcher className="ml-auto" />
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {!profile ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-8">
              <Avatar className="h-14 w-14"><AvatarImage src={profile.avatar} /><AvatarFallback>{profile.name?.[0] || "U"}</AvatarFallback></Avatar>
              <div className="min-w-0">
                <h1 className="font-display font-extrabold text-2xl truncate">{profile.name || "—"}</h1>
                <p className="text-sm text-muted-foreground truncate" data-testid="me-email">{profile.email}</p>
              </div>
            </div>

            <Tabs defaultValue={["youtube", "plano", "pagamentos", "projetos", "uploads"].includes(tab) ? tab : "conta"}>
              <TabsList className="rounded-sm flex-wrap h-auto">
                <TabsTrigger value="conta" data-testid="tab-conta">{t("Dados da conta")}</TabsTrigger>
                <TabsTrigger value="plano" data-testid="tab-plano">{t("Plano e assinatura")}</TabsTrigger>
                <TabsTrigger value="pagamentos" data-testid="tab-pagamentos">{t("Pagamentos")}</TabsTrigger>
                <TabsTrigger value="projetos" data-testid="tab-projetos">{t("Projetos")}</TabsTrigger>
                <TabsTrigger value="uploads" data-testid="tab-uploads">{t("Uploads")}</TabsTrigger>
                <TabsTrigger value="youtube" data-testid="tab-youtube">YouTube</TabsTrigger>
              </TabsList>

              <TabsContent value="conta" className="mt-6 space-y-6">
                <div>
                  <Row label={t("Nome")} value={profile.name || "—"} />
                  <Row label={t("E-mail (login Google — não editável)")} value={profile.email} />
                  <Row label={t("Conta criada em")} value={fmtSP(profile.created_at)} />
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="secondary" className="rounded-sm" onClick={doLogout} data-testid="me-logout-btn">
                    <LogOut className="h-4 w-4 mr-2" /> {t("Sair da conta")}
                  </Button>
                  {!profile.is_admin && (
                    <Button variant="destructive" className="rounded-sm" onClick={deleteAccount} data-testid="me-delete-btn">
                      <Trash2 className="h-4 w-4 mr-2" /> {t("Excluir minha conta")}
                    </Button>
                  )}
                </div>
                {!profile.is_admin && (
                  <p className="text-[11px] text-muted-foreground">{t("A exclusão remove overlays, mídias, fontes, chave do YouTube e conexões. O histórico financeiro é mantido para fins fiscais.")}</p>
                )}
              </TabsContent>

              <TabsContent value="plano" className="mt-6 space-y-4">
                <div className="border border-border/60 rounded-md p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold capitalize" data-testid="me-plano">{profile.plano}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase font-mono ${statusBadge}`} data-testid="me-status">{STATUS_LABEL[profile.status] ? t(STATUS_LABEL[profile.status]) : profile.status}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("Inclui overlays ilimitados, mídias com transparência, YouTube/Twitch, planilhas e link de operador.")}</p>
                </div>
                <div className="border border-border/60 rounded-md p-4">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1"><CalendarClock className="h-3.5 w-3.5 text-primary" /> {t("Acesso válido até")}</p>
                  <p className="font-mono text-lg" data-testid="me-access-until">{profile.access_until ? fmtSP(profile.access_until) : t("Sem limite definido")}</p>
                  {d !== null && profile.access_until && (
                    <p className={`text-xs mt-1 ${d <= 0 ? "text-red-400" : d <= 7 ? "text-amber-400" : "text-emerald-400"}`}>
                      {d <= 0 ? t("Expirado") : t(d === 1 ? "{n} dia restante · vence às 00:00 (São Paulo)" : "{n} dias restantes · vence às 00:00 (São Paulo)", { n: d })}
                    </p>
                  )}
                </div>
                <Button className="rounded-sm" onClick={() => window.open("mailto:agsm.livegrafx@gmail.com?subject=Upgrade%20LiveGrafX", "_blank")} data-testid="me-upgrade-btn">
                  <CreditCard className="h-4 w-4 mr-2" /> {t("Fazer upgrade / renovar")}
                </Button>
              </TabsContent>

              <TabsContent value="pagamentos" className="mt-6">
                {payments === null ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-border/60 rounded-md" data-testid="me-payments-empty">
                    <CreditCard className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">{t("Nenhum pagamento por aqui ainda.")}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t("Quando você renovar seu acesso, o histórico aparece nesta aba.")}</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="me-payments-list">
                    {payments.map((p) => (
                      <div key={p.id} className="border border-border/60 rounded-sm px-3 py-2.5 flex items-center justify-between gap-2 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium">{p.moeda} {Number(p.valor).toFixed(2)} · {p.metodo}</p>
                          <p className="text-[11px] text-muted-foreground">{fmtSP(p.data)} · {t("{n} dias de acesso", { n: p.periodo_dias })}</p>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400 uppercase font-mono flex-shrink-0">{t("pago")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="projetos" className="mt-6">
                {projects === null ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                ) : projects.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-border/60 rounded-md" data-testid="me-projects-empty">
                    <Layers className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">{t("Você ainda não criou nenhum projeto.")}</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="me-projects-list">
                    <p className="text-[11px] text-muted-foreground">{t(projects.length === 1 ? "{n} projeto" : "{n} projetos", { n: projects.length })}</p>
                    {projects.map((o) => (
                      <div key={o.id} className="border border-border/60 rounded-sm px-3 py-2.5 flex items-center justify-between gap-2 text-sm" data-testid={`me-project-${o.id}`}>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{o.nome}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {o.largura}×{o.altura} · {t(o.element_count === 1 ? "{n} elemento" : "{n} elementos", { n: o.element_count || 0 })} · {t("atualizado em {d}", { d: fmtSP(o.updated_at || o.created_at) })}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-sm flex-shrink-0" onClick={() => navigate(`/studio/${o.id}`)}>
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> {t("Abrir no editor")}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="uploads" className="mt-6">
                <UploadsTab mine />
              </TabsContent>

              <TabsContent value="youtube" className="mt-6 space-y-4">
                <div className="border border-border/60 rounded-md p-4 space-y-2" data-testid="me-yt-card">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-display font-bold flex items-center gap-2"><YouTubeIcon /> {t("Chave da API do YouTube")}</span>
                    {ytStatus?.has_key ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400 flex items-center gap-1" data-testid="me-yt-status">
                        <CheckCircle2 className="h-3 w-3" /> {t("configurada")}
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 flex items-center gap-1" data-testid="me-yt-status">
                        <AlertTriangle className="h-3 w-3" /> {t("não configurada")}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("Uma única chave para toda a conta — todos os seus overlays a reutilizam automaticamente.")}
                    {ytStatus?.validada_em && <> {t("Última validação:")} <span className="font-mono">{fmtSP(ytStatus.validada_em)}</span>.</>}
                  </p>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="rounded-sm" onClick={() => setWizardOpen(true)} data-testid="me-yt-configure-btn">
                      <KeyRound className="h-3.5 w-3.5 mr-1.5" /> {ytStatus?.has_key ? t("Trocar chave") : t("Configurar chave")}
                    </Button>
                    {ytStatus?.has_key && (
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={removeKey} data-testid="me-yt-remove-btn">{t("Remover")}</Button>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>

      <YouTubeKeyWizard open={wizardOpen} onOpenChange={setWizardOpen} onSaved={(s) => setYtStatus(s)} />
    </div>
  );
}
